import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Newspaper, Search, Bookmark, BookmarkCheck, Flame, Star,
  ExternalLink, X, RefreshCw, Clock, TrendingUp, Filter,
} from 'lucide-react';

// ─── Utilities ────────────────────────────────────────────────────────────────
function timeAgo(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1)    return 'just now';
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)    return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 4)    return `${w}w ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

// Absolute date — short form for same-year, includes year for older items.
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const opts = d.getFullYear() === now.getFullYear()
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString('en-US', opts);
}

// Hashes a source slug to one of four palette colors. Stable per-source — same
// source always gets the same color. Used for the no-image branded placeholder.
function colorTokenForSource(slug) {
  const palette = ['cyan', 'purple', 'green', 'amber'];
  let h = 0;
  for (const ch of String(slug || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return palette[h % palette.length];
}

// Color-code the topic tags for a bit of visual hierarchy. Falls back to muted.
const CATEGORY_BADGE_CLASS = {
  'Programmatic': 'badge-cyan',
  'CTV':          'badge-purple',
  'Privacy':      'badge-amber',
  'Identity':     'badge-cyan',
  'DSP/SSP':      'badge-cyan',
  'Measurement':  'badge-green',
  'Regulation':   'badge-red',
  'M&A':          'badge-amber',
  'AI':           'badge-purple',
  'Retail Media': 'badge-green',
  'Social':       'badge-muted',
};
const badgeClassFor = (c) => CATEGORY_BADGE_CLASS[c] || 'badge-muted';

const PAGE_SIZE = 24;

// ─── Article Card ─────────────────────────────────────────────────────────────
function ArticleCard({ article, onToggleBookmark }) {
  const [imgError, setImgError] = useState(false);

  // Image priority: article.image_url → source.logo_url → null (renders placeholder)
  const imageUrl = !imgError && (article.image_url || article.source_logo_url);

  // Compose the link — open the article in a new tab. Use canonical URL the backend stored.
  const openSource = (e) => {
    e.preventDefault();
    window.open(article.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="card"
      style={{
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        padding: 0, transition: 'transform 0.15s, border-color 0.15s', cursor: 'pointer',
        borderColor: article.is_featured ? 'color-mix(in srgb, var(--amber) 40%, transparent)' : undefined,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
      onClick={openSource}
    >
      {/* Image / branded placeholder */}
      <div style={{
        height: '160px', position: 'relative', overflow: 'hidden',
        background: 'var(--bg-elevated)',
      }}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            onError={() => setImgError(true)}
            referrerPolicy="no-referrer"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            loading="lazy"
          />
        ) : (
          (() => {
            // Branded placeholder — source name in big display font over a soft
            // gradient using the source's hashed palette color. Keeps the card
            // visually balanced instead of a tiny lost icon in a grey box.
            const c = colorTokenForSource(article.source_slug);
            return (
              <div style={{
                width: '100%', height: '100%',
                background: `linear-gradient(135deg, var(--${c}-dim) 0%, var(--bg-elevated) 70%, var(--bg-elevated) 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative', padding: '0 18px',
              }}>
                <Newspaper size={20} style={{
                  position: 'absolute', top: 14, left: 14,
                  color: `var(--${c})`, opacity: 0.25,
                }} />
                <div style={{
                  fontSize: 24, fontWeight: 800, fontFamily: 'var(--font-display)',
                  color: `var(--${c})`, opacity: 0.85, textAlign: 'center',
                  letterSpacing: '-0.5px', lineHeight: 1.05,
                }}>
                  {article.source_name}
                </div>
              </div>
            );
          })()
        )}

        {/* Trending / Featured overlay badges in top-right of image */}
        <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6 }}>
          {article.is_featured && (
            <span className="badge badge-amber" style={{ fontSize: 10, padding: '3px 7px', backdropFilter: 'blur(4px)' }}>
              <Star size={9} /> Editor's Pick
            </span>
          )}
          {article.is_trending && (
            <span className="badge badge-red" style={{ fontSize: 10, padding: '3px 7px', backdropFilter: 'blur(4px)' }}>
              <Flame size={9} /> Trending
            </span>
          )}
        </div>

        {/* Bookmark button in top-left of image */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleBookmark(article); }}
          title={article.is_bookmarked ? 'Remove bookmark' : 'Save for later'}
          style={{
            position: 'absolute', top: 8, left: 8,
            background: 'rgba(8,11,18,0.7)', backdropFilter: 'blur(4px)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
            width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', padding: 0,
            color: article.is_bookmarked ? 'var(--amber)' : 'var(--text-secondary)',
          }}
        >
          {article.is_bookmarked
            ? <BookmarkCheck size={14} fill="var(--amber)" />
            : <Bookmark size={14} />}
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Source + time + date */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--cyan)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {article.source_name}
          </span>
          <span
            style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}
            title={new Date(article.published_at).toLocaleString()}
          >
            <Clock size={9} /> {timeAgo(article.published_at)} · {formatDate(article.published_at)}
          </span>
        </div>

        {/* Title */}
        <h3 style={{
          fontSize: 14, fontWeight: 700, lineHeight: 1.35, marginBottom: 8,
          color: 'var(--text-primary)', fontFamily: 'var(--font-display)',
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {article.title}
        </h3>

        {/* Summary */}
        {article.summary && (
          <p style={{
            fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary)', marginBottom: 10, flex: 1,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {article.summary}
          </p>
        )}

        {/* Category chips */}
        {article.categories && article.categories.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
            {article.categories.slice(0, 3).map(cat => (
              <span key={cat} className={`badge ${badgeClassFor(cat)}`} style={{ fontSize: 9, padding: '2px 6px' }}>
                {cat}
              </span>
            ))}
            {article.categories.length > 3 && (
              <span style={{ fontSize: 9, color: 'var(--text-muted)', alignSelf: 'center' }}>
                +{article.categories.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Read on source */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 11, color: 'var(--text-muted)', marginTop: 'auto',
        }}>
          <ExternalLink size={10} /> Read on {article.source_name}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function IndustryPulse() {
  const [articles, setArticles]       = useState([]);
  const [total, setTotal]             = useState(0);
  const [offset, setOffset]           = useState(0);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [categories, setCategories]   = useState([]);
  const [sources, setSources]         = useState([]);
  const [stats, setStats]             = useState({ today: 0, this_week: 0, trending: 0, featured: 0 });

  const [activeCategory, setActiveCategory]   = useState(null);  // null = All
  const [activeSource, setActiveSource]       = useState(null);
  const [trendingOnly, setTrendingOnly]       = useState(false);
  const [bookmarkedOnly, setBookmarkedOnly]   = useState(false);
  const [searchQuery, setSearchQuery]         = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Debounce search input — 300ms to avoid spamming the API while typing
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ─── Load articles (refetch on any filter change) ──────────────────────────
  // `currentOffset` is passed explicitly by the caller (the Load More handler
  // reads the latest `offset` from state at click time). We deliberately do NOT
  // close over `offset` here — that was the previous bug: the callback captured
  // a stale offset (0) so "Load More" always refetched page 1.
  const loadArticles = useCallback(async (reset = true, currentOffset = 0) => {
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: reset ? 0 : currentOffset,
      };
      if (activeCategory) params.category = activeCategory;
      if (activeSource)   params.source   = activeSource;
      if (trendingOnly)   params.trending = 'true';
      if (bookmarkedOnly) params.bookmarked = 'true';
      if (searchDebounced) params.q = searchDebounced;

      const { data } = await axios.get('/api/news/articles', { params });
      setArticles(prev => {
        if (reset) return data.articles;
        // Dedup-by-ID insurance: if a fetch cycle inserted new articles between
        // our initial load and this "Load More", offset pagination could return
        // a row we already have. Filter those out so they never render twice.
        const seen = new Set(prev.map(a => a.id));
        const incoming = (data.articles || []).filter(a => !seen.has(a.id));
        return [...prev, ...incoming];
      });
      setTotal(data.total);
      setOffset(reset ? data.articles.length : currentOffset + data.articles.length);
    } catch (err) {
      console.error('[IndustryPulse] load failed', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeCategory, activeSource, trendingOnly, bookmarkedOnly, searchDebounced]);

  // Load sidebar metadata (categories, sources, stats) — once on mount + after fetch cycles
  const loadMeta = useCallback(async () => {
    try {
      const [cats, srcs, st] = await Promise.all([
        axios.get('/api/news/categories'),
        axios.get('/api/news/sources'),
        axios.get('/api/news/stats'),
      ]);
      setCategories(cats.data || []);
      setSources(srcs.data || []);
      setStats(st.data || { today: 0, this_week: 0, trending: 0, featured: 0 });
    } catch (err) {
      console.error('[IndustryPulse] meta load failed', err);
    }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadArticles(true); }, [loadArticles]);

  // Mark Industry Pulse as "seen" so the top-nav Pulse badge clears. The badge
  // logic in App.jsx reads this localStorage key to decide whether to show a count.
  useEffect(() => {
    try { localStorage.setItem('industryPulse:lastSeenAt', String(Date.now())); } catch {}
  }, []);

  // ─── Bookmark toggle ───────────────────────────────────────────────────────
  const toggleBookmark = async (article) => {
    // Optimistic UI — flip first, revert on failure
    const wasBookmarked = article.is_bookmarked;
    setArticles(prev => prev.map(a =>
      a.id === article.id ? { ...a, is_bookmarked: !wasBookmarked } : a
    ));
    try {
      if (wasBookmarked) {
        await axios.delete(`/api/news/bookmarks/${article.id}`);
      } else {
        await axios.post(`/api/news/bookmarks/${article.id}`);
      }
    } catch (err) {
      // Revert
      setArticles(prev => prev.map(a =>
        a.id === article.id ? { ...a, is_bookmarked: wasBookmarked } : a
      ));
      console.error('[IndustryPulse] bookmark toggle failed', err);
    }
  };

  // ─── Derived: active-filter pill count ─────────────────────────────────────
  const activeFilterCount = useMemo(() =>
    (activeCategory ? 1 : 0) + (activeSource ? 1 : 0) +
    (trendingOnly ? 1 : 0) + (bookmarkedOnly ? 1 : 0) + (searchDebounced ? 1 : 0)
  , [activeCategory, activeSource, trendingOnly, bookmarkedOnly, searchDebounced]);

  const clearAllFilters = () => {
    setActiveCategory(null);
    setActiveSource(null);
    setTrendingOnly(false);
    setBookmarkedOnly(false);
    setSearchQuery('');
  };

  const hasMore = articles.length < total;

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Page header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700,
            marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Newspaper size={18} style={{ color: 'var(--cyan)' }} /> Industry Pulse
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Curated AdTech news, refreshed every 3 hours from {sources.length} trusted publishers
          </div>
        </div>

        {/* Quick stats */}
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{
            padding: '6px 12px', background: 'var(--cyan-dim)', border: '1px solid color-mix(in srgb, var(--cyan) 20%, transparent)',
            borderRadius: 'var(--radius-sm)', fontSize: 11,
          }}>
            <span style={{ color: 'var(--text-muted)' }}>Today: </span>
            <strong style={{ color: 'var(--cyan)', fontFamily: 'var(--font-mono)' }}>{stats.today}</strong>
          </div>
          <div style={{
            padding: '6px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', fontSize: 11,
          }}>
            <span style={{ color: 'var(--text-muted)' }}>Week: </span>
            <strong style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{stats.this_week}</strong>
          </div>
          {stats.trending > 0 && (
            <div style={{
              padding: '6px 12px', background: 'var(--red-dim)', border: '1px solid color-mix(in srgb, var(--red) 20%, transparent)',
              borderRadius: 'var(--radius-sm)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <Flame size={11} style={{ color: 'var(--red)' }} />
              <strong style={{ color: 'var(--red)', fontFamily: 'var(--font-mono)' }}>{stats.trending}</strong>
              <span style={{ color: 'var(--text-muted)' }}>trending</span>
            </div>
          )}
        </div>
      </div>

      {/* Search + Bookmark toggle row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 480 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search articles…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="input-field"
            style={{ paddingLeft: 32, width: '100%' }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                padding: 4, display: 'flex',
              }}
              title="Clear search"
            >
              <X size={12} />
            </button>
          )}
        </div>

        <button
          className={bookmarkedOnly ? 'btn btn-primary' : 'btn btn-secondary'}
          onClick={() => setBookmarkedOnly(b => !b)}
          style={{ flexShrink: 0 }}
        >
          {bookmarkedOnly
            ? <><BookmarkCheck size={13} /> My Bookmarks</>
            : <><Bookmark size={13} /> My Bookmarks</>}
        </button>

        <button
          className={trendingOnly ? 'btn btn-primary' : 'btn btn-secondary'}
          onClick={() => setTrendingOnly(t => !t)}
          style={{ flexShrink: 0 }}
        >
          <TrendingUp size={13} /> Trending
        </button>

        {activeFilterCount > 0 && (
          <button className="btn btn-secondary btn-sm" onClick={clearAllFilters} style={{ flexShrink: 0 }}>
            <X size={11} /> Clear ({activeFilterCount})
          </button>
        )}
      </div>

      {/* Category chips */}
      {categories.length > 0 && (
        <div style={{ marginBottom: 14, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Filter size={10} /> Topics:
          </span>
          <button
            onClick={() => setActiveCategory(null)}
            className={`badge ${!activeCategory ? 'badge-cyan' : 'badge-muted'}`}
            style={{ cursor: 'pointer', fontSize: 11, padding: '4px 10px', border: 'none' }}
          >
            All
          </button>
          {categories.map(c => (
            <button
              key={c.category}
              onClick={() => setActiveCategory(activeCategory === c.category ? null : c.category)}
              className={`badge ${activeCategory === c.category ? badgeClassFor(c.category) : 'badge-muted'}`}
              style={{ cursor: 'pointer', fontSize: 11, padding: '4px 10px', border: 'none' }}
              title={`${c.article_count} articles in the last 14 days`}
            >
              {c.category} <span style={{ opacity: 0.6, marginLeft: 4 }}>{c.article_count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Source filter row */}
      {sources.length > 0 && (
        <div style={{ marginBottom: 18, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Sources:
          </span>
          <button
            onClick={() => setActiveSource(null)}
            className={`badge ${!activeSource ? 'badge-cyan' : 'badge-muted'}`}
            style={{ cursor: 'pointer', fontSize: 10, padding: '3px 8px', border: 'none' }}
          >
            All
          </button>
          {sources.map(s => (
            <button
              key={s.slug}
              onClick={() => setActiveSource(activeSource === s.slug ? null : s.slug)}
              className={`badge ${activeSource === s.slug ? 'badge-cyan' : 'badge-muted'}`}
              style={{ cursor: 'pointer', fontSize: 10, padding: '3px 8px', border: 'none' }}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* Loading / Empty / Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <span className="spinner" />
        </div>
      ) : articles.length === 0 ? (
        <div className="empty-state" style={{ padding: '60px 20px' }}>
          <div className="empty-state-icon">
            {bookmarkedOnly
              ? <Bookmark size={32} style={{ opacity: 0.4 }} />
              : <Newspaper size={32} style={{ opacity: 0.4 }} />}
          </div>
          <div className="empty-state-text">
            {bookmarkedOnly
              ? "You haven't bookmarked anything yet"
              : 'No articles match your filters'}
          </div>
          <div className="empty-state-sub">
            {bookmarkedOnly
              ? 'Tap the bookmark icon on any article to save it for later'
              : activeFilterCount > 0
                ? 'Try clearing some filters or check back after the next refresh'
                : 'Articles refresh every 3 hours — first batch may take a few minutes after deploy'}
          </div>
        </div>
      ) : (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 14,
          }}>
            {articles.map(a => (
              <ArticleCard key={a.id} article={a} onToggleBookmark={toggleBookmark} />
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <button
                className="btn btn-secondary"
                onClick={() => loadArticles(false, offset)}
                disabled={loadingMore}
              >
                {loadingMore
                  ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</>
                  : <>Load More ({total - articles.length} remaining)</>}
              </button>
            </div>
          )}

          {!hasMore && articles.length > 0 && (
            <div style={{
              textAlign: 'center', marginTop: 24, color: 'var(--text-muted)',
              fontSize: 11, fontFamily: 'var(--font-mono)',
            }}>
              — End of feed · {total} articles —
            </div>
          )}
        </>
      )}
    </div>
  );
}
